import Star from '@mui/icons-material/Star'
import {Box, Card, CardActionArea, CardContent, Stack, Typography} from '@mui/material'
import {memo, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {SectorPartial} from '../../../@types/entity/Sector'
import type {SpotPartial} from '../../../@types/entity/Spot'
import type {VisitPartial} from '../../../@types/entity/Visit'
import {RectangularSkeleton} from '../../../components/Skeleton/RectangularSkeleton'
import type {PropsWithError} from '../../../components/Skeleton/Skeleton'
import {TextSkeleton} from '../../../components/Skeleton/TextSkeleton'
import {Swiper} from '../../../components/Swiper'
import {getDateDiff} from '../../../hooks/useDate'
import EmptyImage from '../../../resources/empty.svg'

interface SectorsPreviewProps {
  sectors: SectorPartial[]
}

const SectorsPreview = memo(({sectors}: SectorsPreviewProps) => {
  const slides = useMemo(() => {
    return sectors.map((sector) => <Box
      key={sector['@id']}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        height: 1,
      }}>
      <img src={sector.floorPlan ?? EmptyImage} width="100%" alt={sector.name}/>
    </Box>)
  }, [sectors])

  return <Box sx={{
    height: '100%',
    overflow: 'hidden',

    '.swiper': {
      flex: 1,
      height: '100%',
    },

    '.swiper-slides': {
      position: 'relative',
    },
    '.swiper-slide': {
      height: '100%',
    },
    '.swiper-slide img': {
      maxHeight: '100%',
      maxWidth: '100%',
      width: 'auto',
    },
  }}>
    <Swiper
      slides={slides}
      autoplay
    />
  </Box>
})

interface SpotCardProps {
  visit: VisitPartial | undefined
  spot: SpotPartial
  sectors: SectorPartial[]
  link: string
}

export const SpotCard = memo(({visit, spot, sectors, link}: SpotCardProps) => {
  const {t} = useTranslation()

  return <Card sx={{height: 1, minHeight: 150}}>
    <CardActionArea href={link} sx={{height: 1}}>
      <CardContent sx={{height: 1}}>
        <Stack direction="column" sx={{height: 1}}>
          <div className="flex-row">
            {visit?.stared && <Star fontSize="small"/>}
            <Typography variant="h5" className="ellipsis">{spot.name}</Typography>
          </div>
          <div className="flex-row">
            <Box sx={{flex: 1, alignSelf: 'flex-end'}}>
              <Typography variant="body2">
                {visit ? t('page.landing.home.lastVisit', {days: -Math.floor(getDateDiff(visit.aggregate.approximateLastVisitAt))}) : t('page.landing.home.neverVisit')}
              </Typography>
            </Box>
            <Box sx={{width: 150, height: 75}}>
              <SectorsPreview sectors={sectors}/>
            </Box>
          </div>
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
})

export const SpotCardSkeleton = memo(({error}: PropsWithError) => {
  return <Card sx={{height: 1, minHeight: 150}}>
    <CardActionArea sx={{height: 1}}>
      <CardContent sx={{height: 1}}>
        <Stack direction="column" sx={{height: 1}}>
          <div className="flex-row">
            <Typography variant="h5"><TextSkeleton width={200} error={error}/></Typography>
          </div>
          <Stack direction="row" sx={{flex: 1}}>
            <Box sx={{flex: 1, alignSelf: 'flex-end'}}>
              <Typography variant="body2" sx={{mt: 2}}><TextSkeleton width={250} error={error}/></Typography>
            </Box>
            <Box sx={{width: 150}}>
              <RectangularSkeleton width={150} height={75} error={error}/>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
})
